import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faEdit, 
  faTrash, 
  faPlus, 
  faUsers, 
  faUserPlus,
  faUserCheck,
  faFile, 
  faVideo, 
  faHeadphones, 
  faLink, 
  faDownload, 
  faPlay,
  faPause,
  faClock,
  faSpinner,
  faTimes,
  faArrowUpRightFromSquare,
  faCalendarDays
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../Api';
import ResourceUpload from '../../components/formations/ResourceUpload';
import { FORMATION_MANAGER_ROLES } from '../../constants/roles';


// A link resource can be a scheduled online meeting: show when it starts, and whether it has passed.
const formatMeetingDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleString('fr-FR', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

// A meeting only reads as "passée" once it has had time to happen: going grey a minute after the
// start time would mislabel a session that is still running.
const MEETING_GRACE_MINUTES = 60;

const isMeetingPast = (value) => {
  if (!value) return false;
  const date = new Date(value);
  if (isNaN(date.getTime())) return false;
  return date.getTime() + MEETING_GRACE_MINUTES * 60 * 1000 < Date.now();
};

const FormationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formation, setFormation] = useState(null);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedResourceType, setSelectedResourceType] = useState(null);
  const [playingResource, setPlayingResource] = useState(null);
  const [loadingResource, setLoadingResource] = useState(null);
  const [downloadingResource, setDownloadingResource] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [audioStates, setAudioStates] = useState({});
  const videoRefs = useRef({});
  const audioRefs = useRef({});

  useEffect(() => {
    fetchFormation();
    fetchResources();
  }, [id]);

  const fetchFormation = async () => {
    try {
      const response = await api.get(`/formations/${id}`);
      setFormation(response.data.data);
    } catch (error) {
      console.error('Error fetching formation:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchResources = async () => {
    try {
      const response = await api.get(`/formations/${id}/resources`);
      const resourcesData = response.data.data || [];
      
      // Sort resources: videos first, then audio, then files, then links
      const sortedResources = resourcesData.sort((a, b) => {
        const typeOrder = { video: 1, audio: 2, file: 3, link: 4 };
        const orderA = typeOrder[a.type] || 99;
        const orderB = typeOrder[b.type] || 99;
        
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        
        return (a.order || 0) - (b.order || 0);
      });
      
      setResources(sortedResources);
    } catch (error) {
      console.error('Error fetching resources:', error);
    }
  };

  const getStreamUrl = (resourceId) => {
    const baseUrl = process.env.REACT_APP_API_URL || '';
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    return `${baseUrl}/formations/${id}/resources/${resourceId}/stream${token ? `?token=${encodeURIComponent(token)}` : ''}`;
  };

  const getDownloadUrl = (resourceId) => {
    return `${process.env.REACT_APP_API_URL}/formations/${id}/resources/${resourceId}/download-proxy`;
  };

  const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette ressource ?')) {
      return;
    }

    try {
      await api.delete(`/formations/${id}/resources/${resourceId}`);
      fetchResources();
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert(error.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleDownload = async (resource) => {
    setDownloadingResource(resource.id);
    setDownloadProgress(0);
    try {
      const url = getDownloadUrl(resource.id);
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const reader = response.body.getReader();
      const contentLength = response.headers.get('Content-Length');
      let receivedLength = 0;
      let chunks = [];
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }
        chunks.push(value);
        receivedLength += value.length;
        
        if (contentLength) {
          setDownloadProgress(Math.round((receivedLength / contentLength) * 100));
        }
      }

      const blob = new Blob(chunks);
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = resource.file_name || resource.title || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      setDownloadingResource(null);
      setDownloadProgress(0);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Erreur lors du téléchargement');
      setDownloadingResource(null);
      setDownloadProgress(0);
    }
  };

  const handleVideoPlay = (resourceId) => {
    setPlayingResource(resourceId);
    setLoadingResource(resourceId);
  };

  const handleVideoLoaded = (resourceId) => {
    setLoadingResource(null);
  };

  const handleVideoError = (resourceId) => {
    setLoadingResource(null);
    alert('Erreur lors du chargement de la vidéo');
  };

  const toggleAudioPlay = (resourceId) => {
    const audio = audioRefs.current[resourceId];
    if (!audio) return;

    if (audio.paused) {
      setLoadingResource(resourceId);
      audio.play().catch(e => {
        console.error("Audio autoplay failed:", e);
        setLoadingResource(null);
      });
    } else {
      audio.pause();
    }
  };

  const handleAudioTimeUpdate = (resourceId) => {
    const audio = audioRefs.current[resourceId];
    if (audio) {
      setAudioStates(prev => ({
        ...prev,
        [resourceId]: {
          ...prev[resourceId],
          currentTime: audio.currentTime,
          duration: audio.duration || prev[resourceId]?.duration || 0
        }
      }));
    }
  };

  const handleAudioLoaded = (resourceId) => {
    setLoadingResource(null);
    const audio = audioRefs.current[resourceId];
    if (audio) {
      setAudioStates(prev => ({
        ...prev,
        [resourceId]: {
          ...prev[resourceId],
          duration: audio.duration || 0
        }
      }));
    }
  };

  const handleAudioEnded = (resourceId) => {
    setAudioStates(prev => ({
      ...prev,
      [resourceId]: {
        ...prev[resourceId],
        playing: false,
        currentTime: 0
      }
    }));
    setPlayingResource(null);
  };

  const handleAudioError = (resourceId) => {
    setLoadingResource(null);
    alert('Erreur lors du chargement de l\'audio');
    setAudioStates(prev => ({
      ...prev,
      [resourceId]: { ...prev[resourceId], playing: false }
    }));
  };

  const handleAudioProgressChange = (resourceId, value) => {
    const audio = audioRefs.current[resourceId];
    if (audio) {
      audio.currentTime = value;
      setAudioStates(prev => ({
        ...prev,
        [resourceId]: {
          ...prev[resourceId],
          currentTime: value
        }
      }));
    }
  };

  // Initialize audio states when resources load
  useEffect(() => {
    resources.filter(r => r.type === 'audio').forEach(audio => {
      if (!audioStates[audio.id]) {
        setAudioStates(prev => ({
          ...prev,
          [audio.id]: { playing: false, currentTime: 0, duration: 0 }
        }));
      }
    });
  }, [resources]);

  // Download Progress Modal Component
  const DownloadProgressModal = ({ isOpen, progress, onClose, fileName }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 max-w-md w-full">
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-800">Téléchargement</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <FontAwesomeIcon icon={faTimes} className="text-xl" />
            </button>
          </div>
          <p className="text-gray-700 mb-4 text-sm sm:text-base">Téléchargement de: <span className="font-semibold">{fileName}</span></p>
          <div className="w-full bg-gray-200 rounded-full h-2 sm:h-3 mb-4">
            <div 
              className="bg-red-600 h-2 sm:h-3 rounded-full transition-all duration-300 ease-out" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <p className="text-center text-lg font-bold text-red-600">{progress}%</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!formation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Formation non trouvée</p>
      </div>
    );
  }

  const canManage = FORMATION_MANAGER_ROLES.includes(user?.role);

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>
      
      <div className="relative z-10 p-2 sm:p-4 md:p-6 animate-slide-up">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 break-words">{formation.title}</h1>
              {formation.survey && (
                <p className="text-xs sm:text-sm text-gray-600 mt-1 break-words">Survey: {formation.survey.title}</p>
              )}
            </div>
            {canManage && (
              <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                <button
                  onClick={() => navigate(`/formations/${id}/assign`)}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center text-sm sm:text-base"
                >
                  <FontAwesomeIcon icon={faUserPlus} className="mr-1 sm:mr-2" />
                  <span className="hidden xs:inline">Assigner</span>
                  <span className="xs:hidden">Assigner</span>
                </button>
                <button
                  onClick={() => navigate(`/formations/${id}/presence`)}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center text-sm sm:text-base"
                >
                  <FontAwesomeIcon icon={faUserCheck} className="mr-1 sm:mr-2" />
                  <span className="hidden xs:inline">Présence</span>
                  <span className="xs:hidden">Présence</span>
                </button>
                <button
                  onClick={() => navigate(`/formations/${id}/edit`)}
                  className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center"
                >
                  <FontAwesomeIcon icon={faEdit} />
                </button>
              </div>
            )}
          </div>

          {/* Formation Info */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4 sm:p-5 md:p-6 mb-4 sm:mb-5 md:mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Description</h3>
                <p className="text-gray-900">{formation.description || 'Aucune description'}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Audience cible</h3>
                <p className="text-gray-900 capitalize">
                  {formation.target_audience === 'all' ? 'Tous' : formation.target_audience}
                </p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Visibilité</h3>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  formation.visibility === 'public' ? 'text-red-600 bg-red-100' : 'text-gray-600 bg-gray-100'
                }`}>
                  {formation.visibility === 'public' ? 'Public' : 'Privé'}
                </span>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Statut</h3>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  formation.is_active ? 'text-red-600 bg-red-100' : 'text-gray-600 bg-gray-100'
                }`}>
                  {formation.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>

          {/* Resources Section */}
          <div className="bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-3 sm:p-4 md:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-4">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800">Ressources</h2>
              {canManage && (
                <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => {
                      setSelectedResourceType('video');
                      setShowUploadModal(true);
                    }}
                    className="px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 bg-white border-2 border-red-600 text-red-600 rounded-lg hover:bg-red-600 hover:text-white hover:border-red-700 active:bg-red-700 active:text-white transition-all duration-200 text-xs sm:text-sm font-medium flex items-center justify-center shadow-sm hover:shadow-md"
                  >
                    <FontAwesomeIcon icon={faVideo} className="mr-1 sm:mr-2 text-xs sm:text-sm" />
                    <span className="truncate">Ajouter Vidéo</span>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedResourceType('audio');
                      setShowUploadModal(true);
                    }}
                    className="px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 bg-white border-2 border-red-600 text-red-600 rounded-lg hover:bg-red-600 hover:text-white hover:border-red-700 active:bg-red-700 active:text-white transition-all duration-200 text-xs sm:text-sm font-medium flex items-center justify-center shadow-sm hover:shadow-md"
                  >
                    <FontAwesomeIcon icon={faHeadphones} className="mr-1 sm:mr-2 text-xs sm:text-sm" />
                    <span className="truncate">Ajouter Audio</span>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedResourceType('file');
                      setShowUploadModal(true);
                    }}
                    className="px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 bg-white border-2 border-red-600 text-red-600 rounded-lg hover:bg-red-600 hover:text-white hover:border-red-700 active:bg-red-700 active:text-white transition-all duration-200 text-xs sm:text-sm font-medium flex items-center justify-center shadow-sm hover:shadow-md"
                  >
                    <FontAwesomeIcon icon={faFile} className="mr-1 sm:mr-2 text-xs sm:text-sm" />
                    <span className="truncate">Ajouter Fichier</span>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedResourceType('link');
                      setShowUploadModal(true);
                    }}
                    className="px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 bg-white border-2 border-red-600 text-red-600 rounded-lg hover:bg-red-600 hover:text-white hover:border-red-700 active:bg-red-700 active:text-white transition-all duration-200 text-xs sm:text-sm font-medium flex items-center justify-center shadow-sm hover:shadow-md"
                  >
                    <FontAwesomeIcon icon={faLink} className="mr-1 sm:mr-2 text-xs sm:text-sm" />
                    <span className="truncate">Ajouter Lien</span>
                  </button>
                </div>
              )}
            </div>

            {resources.length === 0 ? (
              <div className="bg-white rounded-xl shadow-md p-8 sm:p-12 text-center border border-gray-200">
                <FontAwesomeIcon icon={faFile} className="text-gray-400 text-4xl sm:text-6xl mb-4" />
                <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">Aucune ressource disponible</h3>
                <p className="text-sm sm:text-base text-gray-500">Cette formation ne contient pas encore de ressources.</p>
              </div>
            ) : (
              <div className="space-y-6 sm:space-y-8">
                {/* Videos Section */}
                {resources.filter(r => r.type === 'video').length > 0 && (
                  <div>
                    <div className="flex items-center mb-4 sm:mb-6 flex-wrap gap-2">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-600 rounded-lg flex items-center justify-center mr-2 sm:mr-3 shadow-sm flex-shrink-0">
                        <FontAwesomeIcon icon={faVideo} className="text-white text-sm sm:text-lg" />
                      </div>
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Vidéos</h3>
                      <span className="px-2 sm:px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs sm:text-sm font-medium">
                        {resources.filter(r => r.type === 'video').length}
                      </span>
                    </div>
                    <div className="space-y-4 sm:space-y-6">
                      {resources.filter(r => r.type === 'video').map((video) => (
                        <div key={video.id} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
                          <div className="relative bg-white border-b border-gray-200 w-full" style={{ aspectRatio: '16/9', minHeight: '180px', maxHeight: '100vh' }}>
                            {playingResource === video.id ? (
                              <>
                                {loadingResource === video.id && (
                                  <div className="absolute inset-0 flex items-center justify-center bg-white z-30">
                                    <div className="text-center px-4">
                                      <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-red-600 border-t-transparent mx-auto mb-3 sm:mb-4"></div>
                                      <p className="text-gray-700 text-sm sm:text-lg font-medium">Veuillez patienter, chargement en cours...</p>
                                    </div>
                                  </div>
                                )}
                                <div className="absolute inset-0 flex items-center justify-center bg-white" style={{ display: loadingResource === video.id ? 'none' : 'flex' }}>
                                  <video
                                    ref={(el) => (videoRefs.current[video.id] = el)}
                                    src={getStreamUrl(video.id)}
                                    controls
                                    preload="metadata"
                                    className="w-full h-full object-contain"
                                    onLoadedData={() => handleVideoLoaded(video.id)}
                                    onError={() => handleVideoError(video.id)}
                                    onEnded={() => setPlayingResource(null)}
                                    crossOrigin="anonymous"
                                    style={{ 
                                      backgroundColor: '#fff',
                                      display: loadingResource === video.id ? 'none' : 'block'
                                    }}
                                  >
                                    Votre navigateur ne supporte pas la lecture vidéo.
                                  </video>
                                </div>
                              </>
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center bg-white">
                                <button
                                  onClick={() => handleVideoPlay(video.id)}
                                  className="group relative focus:outline-none transform transition-transform hover:scale-105"
                                >
                                  <div className="absolute inset-0 bg-red-600 rounded-full opacity-90 group-hover:opacity-100 transition-opacity transform scale-125 sm:scale-150"></div>
                                  <FontAwesomeIcon 
                                    icon={faPlay} 
                                    className="relative text-white text-3xl sm:text-4xl ml-2 z-10"
                                  />
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="p-3 sm:p-4 md:p-6 bg-white">
                            <div className="flex flex-col gap-3">
                              <div className="flex-1">
                                <h4 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-2 break-words">{video.title || video.file_name}</h4>
                                {video.description && (
                                  <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 break-words">{video.description}</p>
                                )}
                                <div className="flex items-center flex-wrap gap-3 sm:gap-4 md:gap-6 text-xs sm:text-sm text-gray-500">
                                  {video.duration_seconds && (
                                    <span className="flex items-center">
                                      <FontAwesomeIcon icon={faClock} className="mr-1 sm:mr-2 text-red-600 text-xs sm:text-sm" />
                                      {formatDuration(video.duration_seconds)}
                                    </span>
                                  )}
                                  {video.file_size && (
                                    <span>{formatFileSize(video.file_size)}</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                                <button
                                  onClick={() => handleDownload(video)}
                                  className="flex-1 sm:flex-none px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center justify-center font-medium shadow-sm text-xs sm:text-sm md:text-base"
                                >
                                  <FontAwesomeIcon icon={faDownload} className="mr-1 sm:mr-2" />
                                  Télécharger
                                </button>
                                {canManage && (
                                  <button
                                    onClick={() => handleDeleteResource(video.id)}
                                    className="flex-1 sm:flex-none px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center justify-center font-medium shadow-sm text-xs sm:text-sm md:text-base"
                                  >
                                    <FontAwesomeIcon icon={faTrash} className="mr-1 sm:mr-2" />
                                    Supprimer
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Audio Section */}
                {resources.filter(r => r.type === 'audio').length > 0 && (
                  <div>
                    <div className="flex items-center mb-4 sm:mb-6 flex-wrap gap-2">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-600 rounded-lg flex items-center justify-center mr-2 sm:mr-3 shadow-sm flex-shrink-0">
                        <FontAwesomeIcon icon={faHeadphones} className="text-white text-sm sm:text-lg" />
                      </div>
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Audio</h3>
                      <span className="px-2 sm:px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs sm:text-sm font-medium">
                        {resources.filter(r => r.type === 'audio').length}
                      </span>
                    </div>
                    <div className="space-y-4 sm:space-y-5">
                      {resources.filter(r => r.type === 'audio').map((audio) => (
                        <div key={audio.id} className="bg-white rounded-xl shadow-md border border-gray-200 hover:shadow-xl transition-all duration-300 overflow-hidden">
                          <div className="p-4 sm:p-6">
                            <div className="flex items-start">
                              <div className="flex-1 min-w-0">
                                <div className="mb-3 sm:mb-4">
                                  <h4 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">{audio.title || audio.file_name}</h4>
                                  {audio.description && (
                                    <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">{audio.description}</p>
                                  )}
                                </div>
                                
                                {/* Audio Player */}
                                <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 sm:p-6 mb-3 sm:mb-4 border-2 border-gray-200 shadow-md">
                                  {loadingResource === audio.id && (
                                    <div className="flex items-center justify-center py-2 sm:py-3 mb-3 sm:mb-4 bg-red-50 rounded-lg border border-red-100">
                                      <FontAwesomeIcon icon={faSpinner} className="animate-spin text-red-600 mr-2 sm:mr-3 text-sm sm:text-lg" />
                                      <span className="text-xs sm:text-sm font-medium text-gray-700">Veuillez patienter, chargement en cours...</span>
                                    </div>
                                  )}
                                  
                                  <audio
                                    ref={(el) => {
                                      audioRefs.current[audio.id] = el;
                                    }}
                                    src={getStreamUrl(audio.id)}
                                    preload="none"
                                    className="hidden"
                                    onTimeUpdate={() => handleAudioTimeUpdate(audio.id)}
                                    onLoadedMetadata={() => handleAudioLoaded(audio.id)}
                                    onPlay={() => {
                                      setAudioStates(prev => ({
                                        ...prev,
                                        [audio.id]: { ...prev[audio.id], playing: true }
                                      }));
                                    }}
                                    onPause={() => {
                                      setAudioStates(prev => ({
                                        ...prev,
                                        [audio.id]: { ...prev[audio.id], playing: false }
                                      }));
                                    }}
                                    onEnded={() => handleAudioEnded(audio.id)}
                                    onError={() => handleAudioError(audio.id)}
                                  >
                                    Votre navigateur ne supporte pas la lecture audio.
                                  </audio>

                                  <div className="flex items-center space-x-2 sm:space-x-3 md:space-x-5">
                                    <button
                                      onClick={() => toggleAudioPlay(audio.id)}
                                      disabled={loadingResource === audio.id}
                                      className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 bg-gradient-to-br from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 rounded-full flex items-center justify-center text-white shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                                    >
                                      <FontAwesomeIcon 
                                        icon={audioStates[audio.id]?.playing ? faPause : faPlay} 
                                        className="text-base sm:text-lg md:text-xl ml-0.5"
                                      />
                                    </button>

                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-4">
                                        <span className="text-xs font-semibold text-gray-700 w-10 sm:w-12 md:w-20 text-right tabular-nums flex-shrink-0">
                                          {formatDuration(audioStates[audio.id]?.currentTime || 0)}
                                        </span>
                                        <div className="flex-1 relative min-w-0" style={{ paddingTop: '6px', paddingBottom: '6px' }}>
                                          <input
                                            type="range"
                                            min="0"
                                            max={audioStates[audio.id]?.duration || 100}
                                            value={audioStates[audio.id]?.currentTime || 0}
                                            onChange={(e) => handleAudioProgressChange(audio.id, parseFloat(e.target.value))}
                                            disabled={!audioStates[audio.id]?.duration}
                                            className="w-full h-2 sm:h-3 bg-gray-200 rounded-full appearance-none cursor-pointer audio-slider disabled:cursor-not-allowed focus:outline-none"
                                            style={{
                                              background: `linear-gradient(to right, #dc2626 0%, #dc2626 ${audioStates[audio.id]?.duration ? ((audioStates[audio.id]?.currentTime || 0) / audioStates[audio.id].duration) * 100 : 0}%, #e5e7eb ${audioStates[audio.id]?.duration ? ((audioStates[audio.id]?.currentTime || 0) / audioStates[audio.id].duration) * 100 : 0}%, #e5e7eb 100%)`
                                            }}
                                          />
                                        </div>
                                        <span className="text-xs font-semibold text-gray-700 w-10 sm:w-12 md:w-20 tabular-nums flex-shrink-0">
                                          {formatDuration(audioStates[audio.id]?.duration || 0)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="flex flex-col gap-3 pt-3 border-t border-gray-200">
                                  <div className="flex items-center flex-wrap gap-3 sm:gap-4 md:gap-6 text-xs sm:text-sm">
                                    {audio.duration_seconds && (
                                      <span className="flex items-center text-gray-600 font-medium">
                                        <FontAwesomeIcon icon={faClock} className="mr-1 sm:mr-2 text-red-600 text-xs sm:text-sm" />
                                        {formatDuration(audio.duration_seconds)}
                                      </span>
                                    )}
                                    {audio.file_size && (
                                      <span className="text-gray-500">{formatFileSize(audio.file_size)}</span>
                                    )}
                                  </div>
                                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                                    <button
                                      onClick={() => handleDownload(audio)}
                                      className="flex-1 sm:flex-none px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all duration-200 flex items-center justify-center font-medium shadow-sm hover:shadow-md text-xs sm:text-sm md:text-base"
                                    >
                                      <FontAwesomeIcon icon={faDownload} className="mr-1 sm:mr-2" />
                                      Télécharger
                                    </button>
                                    {canManage && (
                                      <button
                                        onClick={() => handleDeleteResource(audio.id)}
                                        className="flex-1 sm:flex-none px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all duration-200 flex items-center justify-center font-medium shadow-sm hover:shadow-md text-xs sm:text-sm md:text-base"
                                      >
                                        <FontAwesomeIcon icon={faTrash} className="mr-1 sm:mr-2" />
                                        Supprimer
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Files Section */}
                {resources.filter(r => r.type === 'file').length > 0 && (
                  <div>
                    <div className="flex items-center mb-4 sm:mb-6 flex-wrap gap-2">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-600 rounded-lg flex items-center justify-center mr-2 sm:mr-3 shadow-sm flex-shrink-0">
                        <FontAwesomeIcon icon={faFile} className="text-white text-sm sm:text-lg" />
                      </div>
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Fichiers</h3>
                      <span className="px-2 sm:px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs sm:text-sm font-medium">
                        {resources.filter(r => r.type === 'file').length}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                      {resources.filter(r => r.type === 'file').map((file) => {
                        const isPdf = file.mime?.includes('pdf');
                        const isImage = file.mime?.includes('image');
                        const streamUrl = getStreamUrl(file.id);
                        
                        return (
                          <div key={file.id} className="bg-white rounded-xl shadow-lg border-2 border-gray-200 hover:border-red-200 transition-all duration-300 overflow-hidden">
                            <div className="bg-gradient-to-br from-red-50 via-white to-gray-50 p-4 sm:p-6 border-b-2 border-gray-200">
                              <div className="flex items-center justify-end mb-3 sm:mb-4">
                                {file.file_size && (
                                  <span className="text-xs font-semibold text-gray-600 bg-white px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg border border-gray-200 shadow-sm">
                                    {formatFileSize(file.file_size)}
                                  </span>
                                )}
                              </div>
                              <h4 className="text-base sm:text-lg font-bold text-gray-900 line-clamp-2 mb-2" title={file.title || file.file_name}>
                                {file.title || file.file_name}
                              </h4>
                              {file.description && (
                                <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 leading-relaxed">{file.description}</p>
                              )}
                            </div>
                            
                            {(isPdf || isImage) && (
                              <div className="border-b-2 border-gray-200 bg-gradient-to-br from-gray-50 to-white overflow-hidden">
                                {isImage ? (
                                  <div className="relative h-40 sm:h-56 overflow-hidden">
                                    <img
                                      src={streamUrl}
                                      alt={file.title || file.file_name}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <div className="h-40 sm:h-56 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-50 border-b-2 border-gray-200">
                                    <iframe
                                      src={streamUrl}
                                      className="w-full h-full"
                                      title={file.title || file.file_name}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                            
                            <div className="p-4 sm:p-5 bg-gradient-to-br from-gray-50 to-white">
                              <div className="flex flex-col sm:flex-row gap-2">
                                <button
                                  onClick={() => handleDownload(file)}
                                  className="flex-1 px-4 sm:px-5 py-2.5 sm:py-3.5 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg transition-colors duration-200 flex items-center justify-center font-semibold shadow-md text-sm sm:text-base"
                                >
                                  <FontAwesomeIcon icon={faDownload} className="mr-2 text-base sm:text-lg" />
                                  Télécharger
                                </button>
                                {canManage && (
                                  <button
                                    onClick={() => handleDeleteResource(file.id)}
                                    className="px-4 sm:px-5 py-2.5 sm:py-3.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 flex items-center justify-center font-semibold shadow-md text-sm sm:text-base"
                                  >
                                    <FontAwesomeIcon icon={faTrash} className="mr-2" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Links Section */}
                {resources.filter(r => r.type === 'link').length > 0 && (
                  <div>
                    <div className="flex items-center mb-4 sm:mb-6 flex-wrap gap-2">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-600 rounded-lg flex items-center justify-center mr-2 sm:mr-3 shadow-sm flex-shrink-0">
                        <FontAwesomeIcon icon={faLink} className="text-white text-sm sm:text-lg" />
                      </div>
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Liens externes</h3>
                      <span className="px-2 sm:px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs sm:text-sm font-medium">
                        {resources.filter(r => r.type === 'link').length}
                      </span>
                    </div>
                    <div className="space-y-3 sm:space-y-4">
                      {resources.filter(r => r.type === 'link').map((link) => (
                        <div key={link.id} className="bg-white rounded-xl shadow-md border-2 border-gray-200 hover:border-red-200 hover:shadow-xl transition-all duration-300">
                          <div className="p-3 sm:p-4 md:p-6">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 mb-2 break-words group-hover:text-red-600 transition-colors">
                                  {link.title || link.external_url}
                                </h4>
                                {link.is_meeting && link.meeting_at && (
                                  <div className={`mb-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium ${
                                    isMeetingPast(link.meeting_at)
                                      ? 'bg-gray-100 text-gray-600 border border-gray-200'
                                      : 'bg-red-50 text-red-700 border border-red-200'
                                  }`}>
                                    <FontAwesomeIcon icon={faCalendarDays} />
                                    <span>
                                      {isMeetingPast(link.meeting_at) ? 'Réunion passée : ' : 'Réunion en ligne : '}
                                      {formatMeetingDate(link.meeting_at)}
                                    </span>
                                  </div>
                                )}
                                {link.description && (
                                  <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3 leading-relaxed break-words">{link.description}</p>
                                )}
                                <div className="flex items-start sm:items-center space-x-2">
                                  <FontAwesomeIcon icon={faLink} className="text-red-600 text-xs flex-shrink-0 mt-0.5 sm:mt-0" />
                                  <p className="text-xs text-gray-500 break-all font-mono">{link.external_url}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 w-full sm:w-auto">
                                <a
                                  href={link.external_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex-shrink-0"
                                >
                                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-50 rounded-lg flex items-center justify-center group-hover:bg-red-100 transition-colors border-2 border-red-100 group-hover:border-red-200">
                                    <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-red-600 text-sm sm:text-base md:text-lg" />
                                  </div>
                                </a>
                                {canManage && (
                                  <button
                                    onClick={() => handleDeleteResource(link.id)}
                                    className="flex-1 sm:flex-none px-3 sm:px-4 md:px-5 py-2 sm:py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center justify-center font-medium shadow-sm text-xs sm:text-sm md:text-base"
                                  >
                                    <FontAwesomeIcon icon={faTrash} className="mr-1 sm:mr-2" />
                                    Supprimer
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <ResourceUpload
          formationId={id}
          resourceType={selectedResourceType}
          onClose={() => {
            setShowUploadModal(false);
            setSelectedResourceType(null);
          }}
          onSuccess={() => {
            fetchResources();
            setShowUploadModal(false);
            setSelectedResourceType(null);
          }}
        />
      )}

      {/* Download Progress Modal */}
      <DownloadProgressModal 
        isOpen={!!downloadingResource} 
        progress={downloadProgress} 
        onClose={() => setDownloadingResource(null)} 
        fileName={resources.find(r => r.id === downloadingResource)?.file_name || 'Fichier'}
      />
    </div>
  );
};

export default FormationDetail;

